import React, { useState, useEffect, useCallback } from 'react';
import QuestionLearnButton from '../full_mock_test/attempt/QuestionLearnButton';
import '../../styles/ExerciseLayout.css';

const ConversationQuestion = ({ question, questionNumber, isExample, answer, onSelect, showHint, isReviewMode, isCorrect, isHydrated }) => {
  const rawText = question.question_text.replace(/\(Example\)/gi, '').trim();
  const beforeGap = rawText.split('{{big_gap}}')[0].trim();

  // Parse dialogue: "Name: text Name: text" — split on "Name:" at word boundaries
  // Find all "Name:" patterns (capitalized word followed by colon at start or after space)
  const lines = [];
  const speakerRegex = /(?:^|\s)([A-Z][a-z]+):\s*/g;
  const speakers = [];
  let match;
  while ((match = speakerRegex.exec(beforeGap)) !== null) {
    speakers.push({ name: match[1], index: match.index + (match[0].length - match[0].trimStart().length), end: match.index + match[0].length });
  }
  for (let i = 0; i < speakers.length; i++) {
    const textStart = speakers[i].end;
    const textEnd = i + 1 < speakers.length ? speakers[i + 1].index : beforeGap.length;
    lines.push({ speaker: speakers[i].name, text: beforeGap.slice(textStart, textEnd).trim() });
  }
  // Fallback if parsing fails
  if (lines.length === 0) {
    lines.push({ speaker: '', text: beforeGap });
  }

  const isAnswerCorrect = isCorrect === true || isCorrect === 'true';
  const options = question.options || [];

  const selectedValue = isExample
    ? (question.accepted_answers && question.accepted_answers[0])
    : (showHint || (isReviewMode && isAnswerCorrect))
      ? question.accepted_answers[0]
      : answer;

  return (
    <div className={`p-4 rounded-2xl border-2 transition-all ${isExample ? 'bg-blue-50/50 border-blue-100' : 'bg-white border-slate-100 shadow-sm'}`}>
      {/* Question number */}
      <div className="flex items-center gap-2 mb-3">
        <span className={`shrink-0 font-black px-2.5 py-1 rounded-xl text-sm border shadow-sm ${isExample ? 'text-white bg-amber-500 border-amber-500' : 'text-slate-400 bg-slate-50 border-slate-100'}`}>
          {isExample ? 'e.g.' : questionNumber}
        </span>
        {isReviewMode && !isExample && (
          <div className="flex items-center gap-1 ml-auto">
            {isAnswerCorrect
              ? <span className="correct-checkmark">✓</span>
              : <span className="incorrect-checkmark">✗</span>
            }
            <QuestionLearnButton question={question} />
          </div>
        )}
      </div>

      {/* Dialogue lines */}
      <div className="space-y-2 mb-3">
        {lines.map((line, idx) => {
          const isLastLine = idx === lines.length - 1;
          return (
            <div key={idx} className="flex gap-3">
              <span className="font-black text-slate-500 w-16 shrink-0 text-right text-sm pt-0.5">{line.speaker}:</span>
              <span className={`font-bold text-sm leading-relaxed ${isLastLine ? 'text-slate-400 italic' : 'text-slate-700'}`}>
                {isLastLine && !line.text ? '...' : line.text}
              </span>
            </div>
          );
        })}
      </div>

      {/* Options A/B/C */}
      <div className="ml-[76px] space-y-1.5">
        {options.map(option => {
          const optionId = option.id;
          const isSelected = selectedValue === optionId;
          const isThisCorrect = question.accepted_answers?.includes(optionId);
          const isIncorrect = isReviewMode && !isAnswerCorrect && answer === optionId && !isThisCorrect;

          return (
            <div
              key={optionId}
              onClick={() => !isReviewMode && !showHint && !isExample && isHydrated && onSelect(question.id, optionId)}
              className={`flex items-center gap-3 px-3 py-2 rounded-xl border-2 transition-all ${isExample || isReviewMode || showHint ? 'cursor-default' : 'cursor-pointer'}
                ${isSelected && !isReviewMode && !showHint ? 'border-primary-blue bg-blue-50 shadow-sm' : ''}
                ${isSelected && showHint ? 'border-amber-400 bg-amber-50 shadow-sm' : ''}
                ${isReviewMode && isThisCorrect ? 'border-green-500 bg-green-50/50' : ''}
                ${isIncorrect ? 'border-red-400 bg-red-50/50' : ''}
                ${!isSelected && !isIncorrect && !(isReviewMode && isThisCorrect) ? 'border-slate-100 hover:border-slate-200' : ''}
              `}
            >
              <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 text-xs font-black
                ${isSelected && !isReviewMode && !showHint ? 'bg-primary-blue border-primary-blue text-white' : ''}
                ${isSelected && showHint ? 'bg-amber-500 border-amber-500 text-white' : ''}
                ${isReviewMode && isThisCorrect ? 'bg-green-500 border-green-500 text-white' : ''}
                ${isIncorrect ? 'bg-red-400 border-red-400 text-white' : ''}
                ${!isSelected && !isIncorrect && !(isReviewMode && isThisCorrect) ? 'border-slate-300 text-slate-400' : ''}
              `}>
                {optionId}
              </span>
              <span className="font-bold text-sm text-slate-700">{option.option_text}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MoversReadingPart2 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        if (Object.keys(migratedAnswers).length > 0) {
          setAnswers(migratedAnswers);
          setIsHydrated(true);
          return;
        }
      }

      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData) {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode, exerciseData.questions]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId || isReviewMode) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach(qId => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated, isReviewMode]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (!isHydrated) return;
    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);
    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const sortedQuestions = [...exerciseData.questions].sort((a, b) => a.order_index - b.order_index);
  const exampleQuestion = sortedQuestions.find(q => q.points === 0);
  const mainQuestions = sortedQuestions.filter(q => q.points > 0);

  const handleSelect = (questionId, optionId) => {
    setAnswers(prev => ({ ...prev, [questionId]: optionId }));
  };

  return (
    <div className="w-full h-full p-1 md:px-10 overflow-y-auto custom-scrollbar">
      <div className="max-w-3xl mx-auto w-full py-4 space-y-3">
        {exampleQuestion && (
          <ConversationQuestion
            question={exampleQuestion}
            isExample={true}
            answer={answers[exampleQuestion.id]}
            onSelect={handleSelect}
            isReviewMode={isReviewMode}
            isCorrect={exampleQuestion.is_correct}
            isHydrated={isHydrated}
          />
        )}
        {mainQuestions.map((q, index) => (
          <ConversationQuestion
            key={q.id}
            question={q}
            questionNumber={index + 1}
            isExample={false}
            answer={answers[q.id]}
            onSelect={handleSelect}
            showHint={showHint}
            isReviewMode={isReviewMode}
            isCorrect={q.is_correct}
            isHydrated={isHydrated}
          />
        ))}
      </div>
    </div>
  );
};

export default MoversReadingPart2;
